import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const extractErrorMessage = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (typeof detail?.message === "string" && detail.message.trim()) {
    return detail.message.trim();
  }
  return fallbackMessage;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("it-IT", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

function TrashMeta({ item }) {
  return (
    <div className="mt-3 grid gap-1 text-xs text-slate-500 sm:grid-cols-2">
      <p>
        <span className="font-medium text-slate-600">Rimosso:</span>{" "}
        {formatDateTime(item.deleted_at)}
      </p>
      <p>
        <span className="font-medium text-slate-600">Scadenza:</span>{" "}
        {formatDateTime(item.purge_after)}
      </p>
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div className="rounded-sm border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500">
      {children}
    </div>
  );
}

export function TrashPage() {
  const [activeTab, setActiveTab] = useState("staff");
  const [staff, setStaff] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringStaffId, setRestoringStaffId] = useState("");
  const [restoringCustomerId, setRestoringCustomerId] = useState("");

  const loadTrash = async () => {
    setLoading(true);
    try {
      const [staffRes, customersRes] = await Promise.all([
        api.get("/trash/staff"),
        api.get("/trash/customers"),
      ]);
      setStaff(Array.isArray(staffRes.data) ? staffRes.data : []);
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Caricamento cestino non riuscito."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrash();
  }, []);

  const sortedStaff = useMemo(() => {
    return [...staff].sort((left, right) => {
      return (right.deleted_at || "").localeCompare(left.deleted_at || "");
    });
  }, [staff]);

  const sortedCustomers = useMemo(() => {
    return [...customers].sort((left, right) => {
      return (right.deleted_at || "").localeCompare(left.deleted_at || "");
    });
  }, [customers]);

  const restoreStaff = async (member) => {
    setRestoringStaffId(member.id);
    try {
      await api.post(`/staff/${member.id}/restore`);
      toast.success("Staff ripristinato.");
      const response = await api.get("/trash/staff");
      setStaff(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Impossibile ripristinare lo staff."));
    } finally {
      setRestoringStaffId("");
    }
  };

  const restoreCustomer = async (customer) => {
    setRestoringCustomerId(customer.id);
    try {
      await api.post(`/customers/${customer.id}/restore`);
      toast.success("Cliente ripristinato.");
      const response = await api.get("/trash/customers");
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Impossibile ripristinare il cliente."));
    } finally {
      setRestoringCustomerId("");
    }
  };

  const activeItems = activeTab === "staff" ? sortedStaff : sortedCustomers;

  return (
    <div className="space-y-6" data-testid="trash-page">
      <section className="space-y-2">
        <h1 className="text-2xl font-semibold section-title" data-testid="trash-title">
          Cestino
        </h1>
        <p className="max-w-3xl text-sm text-slate-500" data-testid="trash-description">
          Elementi rimossi dalle liste operative. Puoi ripristinarli senza cancellare lo storico.
        </p>
      </section>

      <section className="bg-white border border-slate-200 rounded-sm p-6" data-testid="trash-content">
        <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-4" data-testid="trash-tabs">
          <button
            type="button"
            onClick={() => setActiveTab("staff")}
            className={`rounded-sm px-4 py-2 text-sm font-medium ${
              activeTab === "staff" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
            }`}
            data-testid="trash-tab-staff"
          >
            Staff ({sortedStaff.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("customers")}
            className={`rounded-sm px-4 py-2 text-sm font-medium ${
              activeTab === "customers" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
            }`}
            data-testid="trash-tab-customers"
          >
            Clienti ({sortedCustomers.length})
          </button>
        </div>

        <div className="mt-5 space-y-3" data-testid="trash-list">
          {loading ? (
            <p className="text-sm text-slate-500" data-testid="trash-loading">
              Caricamento cestino...
            </p>
          ) : activeItems.length === 0 ? (
            <EmptyState>
              {activeTab === "staff"
                ? "Nessuno staff nel cestino."
                : "Nessun cliente nel cestino."}
            </EmptyState>
          ) : activeTab === "staff" ? (
            sortedStaff.map((member) => {
              const isRestoring = restoringStaffId === member.id;
              return (
                <article
                  key={member.id}
                  className="rounded-sm border border-slate-200 px-4 py-3"
                  data-testid={`trash-staff-${member.id}`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900">{member.name}</p>
                      <p className="text-xs text-slate-500">
                        {member.specialties?.length ? member.specialties.join(", ") : "Nessuna specialita"}
                      </p>
                      <TrashMeta item={member} />
                    </div>
                    <button
                      type="button"
                      onClick={() => restoreStaff(member)}
                      disabled={isRestoring}
                      className="h-9 rounded-sm border border-slate-200 px-3 text-sm font-medium disabled:opacity-60"
                      data-testid={`trash-staff-restore-${member.id}`}
                    >
                      {isRestoring ? "Ripristino..." : "Ripristina"}
                    </button>
                  </div>
                </article>
              );
            })
          ) : (
            sortedCustomers.map((customer) => {
              const isRestoring = restoringCustomerId === customer.id;
              const contact = [customer.email, customer.phone].filter(Boolean).join(" - ");
              return (
                <article
                  key={customer.id}
                  className="rounded-sm border border-slate-200 px-4 py-3"
                  data-testid={`trash-customer-${customer.id}`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900">{customer.name}</p>
                      <p className="text-xs text-slate-500">{contact || "Nessun contatto"}</p>
                      <TrashMeta item={customer} />
                    </div>
                    <button
                      type="button"
                      onClick={() => restoreCustomer(customer)}
                      disabled={isRestoring}
                      className="h-9 rounded-sm border border-slate-200 px-3 text-sm font-medium disabled:opacity-60"
                      data-testid={`trash-customer-restore-${customer.id}`}
                    >
                      {isRestoring ? "Ripristino..." : "Ripristina"}
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
